import { useCallback, useState } from 'react';
import { Box, Button, CardContent, Paper, TextField, Typography } from '@mui/material';
import { Link, Navigate } from 'react-router-dom';
import { useAuthCore } from '../../core/modules/auth';
import { ERROR } from '../../core/constants/errorCode';
import { WELCOME } from '../../core/router/routes';
import { useGlobalCore } from '../../core/modules/global';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { isEmpty } from 'lodash';

interface FormValues {
  email: string;
  password: string;
}

const schema = yup.object({
	email: yup.string().email('Formato incorrecto').required('Campo requerido'),
	password: yup.string().required('Campo requerido')
});

const defaultValues = {
	email: '',
	password: '',
};

const SignIn = () => {
  const { token, errorCode, login } = useAuthCore();
  const { closeAllDialog } = useGlobalCore();

  const [loading, setLoading] = useState(false);

  const { control, formState, handleSubmit } = useForm<FormValues>({
		mode: 'onChange',
		defaultValues,
		resolver: yupResolver(schema)
	});

	const { isValid, dirtyFields, errors } = formState;

  const onSubmit = useCallback((values: FormValues) => {
    setLoading(true);
    login(
      values,
      () => {
				closeAllDialog();
        setLoading(false);
      }, () => {
        setLoading(false);
      }
    );
  }, [closeAllDialog, login]);

  if (token) {
    return <Navigate to={WELCOME} replace />;
  }

  return (
    <div className="flex min-w-0 flex-1 flex-col items-center sm:flex-row sm:justify-center md:items-start md:justify-start">
			<Paper className="h-full w-full px-16 py-8 ltr:border-r-1 rtl:border-l-1 sm:h-auto sm:w-auto sm:rounded-xl sm:p-48 sm:shadow md:flex md:h-full md:w-1/2 md:items-center md:justify-end md:rounded-none md:p-64 md:shadow-none">
				<CardContent className="mx-auto w-full max-w-320 sm:mx-0 sm:w-320">
					<div className="flex items-center gap-12">
						<img
							className="w-48"
							src="/assets/images/logo/Logo Revaluex.png"
							alt="REVALUEX logo"
						/>
						<span className="text-24 font-bold" style={{ color: '#E91E63' }}>
							REVALUEX
						</span>
					</div>

					<Typography className="mt-32 text-4xl font-extrabold leading-tight tracking-tight">
						Iniciar Sesión
					</Typography>

          <div className="w-full">
            <form
              name="loginForm"
              noValidate
              className="mt-32 flex w-full flex-col justify-center"
              onSubmit={handleSubmit(onSubmit)}
            >
              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    className="mb-24"
                    label="Email"
                    autoFocus
                    type="email"
                    error={!!errors.email}
                    helperText={errors?.email?.message}
                    variant="outlined"
                    required
                    fullWidth
                  />
                )}
              />

              <Controller
                name="password"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    className="mb-24"
                    label="Contraseña"
                    type="password"
                    error={!!errors.password}
                    helperText={errors?.password?.message}
                    variant="outlined"
                    required
                    fullWidth
                  />
                )}
              />

              <div className="flex flex-col items-center justify-center sm:flex-row sm:justify-end mb-24">
                <Link
                  className="text-md font-medium"
                  to="/#"
                >
                  Olvidaste la contraseña?
                </Link>
              </div>

							{errorCode === ERROR.ERR_400 && (
								<Typography color="error" className="text-center mb-8">
									Los datos son incorrectos
								</Typography>
							)}

              <Button
                variant="contained"
                color="secondary"
                className="w-full"
                aria-label="Iniciar Sesión"
                disabled={isEmpty(dirtyFields) || !isValid || loading}
                type="submit"
                size="large"
              >
                Iniciar Sesión
              </Button>
            </form>
          </div>
				</CardContent>
			</Paper>

			<Box
				className="relative hidden h-full flex-auto items-center justify-center overflow-hidden p-64 md:flex lg:px-112"
				sx={{ backgroundColor: 'primary.dark', color: 'primary.contrastText' }}
			>
				<svg
					className="pointer-events-none absolute inset-0"
					viewBox="0 0 960 540"
					width="100%"
					height="100%"
					preserveAspectRatio="xMidYMax slice"
					xmlns="http://www.w3.org/2000/svg"
				>
					<Box
						component="g"
						className="opacity-5"
						fill="none"
						stroke="currentColor"
						strokeWidth="100"
					>
						<circle
							r="234"
							cx="196"
							cy="23"
						/>
						<circle
							r="234"
							cx="790"
							cy="491"
						/>
					</Box>
				</svg>
				<Box
					component="svg"
					className="absolute -right-64 -top-64 opacity-20"
					sx={{ color: 'primary.light' }}
					viewBox="0 0 220 192"
					width="220px"
					height="192px"
					fill="none"
				>
					<defs>
						<pattern
							id="837c3e70-6c3a-44e6-8854-cc48c737b659"
							x="0"
							y="0"
							width="20"
							height="20"
							patternUnits="userSpaceOnUse"
						>
							<rect
								x="0"
								y="0"
								width="4"
								height="4"
								fill="currentColor"
							/>
						</pattern>
					</defs>
					<rect
						width="220"
						height="192"
						fill="url(#837c3e70-6c3a-44e6-8854-cc48c737b659)"
					/>
				</Box>

				<div className="relative z-10 w-full max-w-2xl">
					<div className="text-7xl font-bold leading-none text-gray-100">
						<div>Bienvenidos a</div>
						<div>Exit Flow</div>
					</div>
					<div className="mt-24 text-lg leading-6 tracking-tight text-gray-400">
            Ofrecemos una solución a empresas de retail u operadores logísticos, en la gestión de los productos en devolución, deterioros, saldos de tienda, discontinuos, sobrestock entre otras, optimizando su flujo de salida de la manera más rentable, rápida y sustentable posible.
					</div>
				</div>
			</Box>
		</div>
  );
};

export default SignIn;
